import { useEffect, useState } from "react";
import { api, apiError } from "../lib/api";
import { toast } from "sonner";
import { Shield, Save, Check } from "lucide-react";
import { ROLE_LABELS } from "../lib/constants";

export function PermissionsManager() {
  const [pages, setPages] = useState([]);
  const [rolePerms, setRolePerms] = useState({});
  const [users, setUsers] = useState([]);
  const [selUser, setSelUser] = useState("");
  const [inherit, setInherit] = useState(true);
  const [userPages, setUserPages] = useState([]);
  const [savingRole, setSavingRole] = useState("");
  const [savingUser, setSavingUser] = useState(false);

  const load = async () => {
    try {
      const [p, rp, u] = await Promise.all([api.get("/pages"), api.get("/permissions/roles"), api.get("/users")]);
      setPages(p.data); setRolePerms(rp.data); setUsers(u.data);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);

  const selectedUser = users.find((x) => x.id === selUser);
  useEffect(() => {
    if (!selectedUser) return;
    const ap = selectedUser.allowed_pages;
    const hasOverride = Array.isArray(ap);
    setInherit(!hasOverride);
    setUserPages(hasOverride ? ap : (rolePerms[selectedUser.role] || []));
  }, [selUser]); // eslint-disable-line

  const toggleRolePage = (role, key) =>
    setRolePerms((rp) => ({ ...rp, [role]: (rp[role] || []).includes(key) ? rp[role].filter((k) => k !== key) : [...(rp[role] || []), key] }));

  const saveRole = async (role) => {
    setSavingRole(role);
    try { await api.put(`/permissions/roles/${role}`, { pages: rolePerms[role] || [] }); toast.success(`${ROLE_LABELS[role]} yetkileri kaydedildi`); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSavingRole(""); }
  };

  const toggleUserPage = (key) =>
    setUserPages((up) => up.includes(key) ? up.filter((k) => k !== key) : [...up, key]);

  const saveUser = async () => {
    setSavingUser(true);
    try {
      await api.put(`/users/${selUser}/permissions`, inherit ? { inherit: true } : { pages: userPages });
      toast.success("Kullanıcı yetkileri kaydedildi");
      await load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSavingUser(false); }
  };

  const PageCheck = ({ checked, onChange, disabled, testid, label }) => (
    <label className={`flex items-center gap-2.5 rounded-xl border px-3.5 py-2.5 text-sm ${disabled ? "cursor-not-allowed opacity-60" : "cursor-pointer hover:bg-secondary/40"} ${checked ? "border-primary bg-primary/5" : "border-border"}`}>
      <button type="button" data-testid={testid} disabled={disabled} onClick={() => !disabled && onChange()}
        className={`flex h-5 w-5 items-center justify-center rounded-md border ${checked ? "border-primary bg-primary text-white" : "border-muted-foreground/40 bg-white"}`}>
        {checked && <Check className="h-3.5 w-3.5" />}
      </button>
      <span className="font-medium">{label}</span>
    </label>
  );

  return (
    <div className="space-y-6" data-testid="permissions-manager">
      {/* Role-based */}
      <div className="rounded-2xl border border-border bg-white p-5 shadow-sm">
        <div className="mb-1 flex items-center gap-2">
          <Shield className="h-5 w-5 text-primary" />
          <h3 className="font-display text-lg font-bold">Rol Bazlı Yetkiler</h3>
        </div>
        <p className="mb-4 text-sm text-muted-foreground">Bir rolün varsayılan olarak girebileceği sayfalar. Kullanıcıya özel ayar yapılmadıkça bu geçerlidir.</p>

        <div className="mb-4 rounded-xl border border-dashed border-border bg-secondary/30 px-4 py-3 text-sm">
          <span className="font-semibold">{ROLE_LABELS.admin}</span> her zaman tüm sayfalara erişir (değiştirilemez).
        </div>

        {["staff", "production"].map((role) => (
          <div key={role} className="mb-4 last:mb-0 rounded-xl border border-border p-4">
            <div className="mb-3 flex items-center justify-between">
              <span className="font-semibold">{ROLE_LABELS[role] || role}</span>
              <button data-testid={`role-perm-save-${role}`} onClick={() => saveRole(role)} disabled={savingRole === role}
                className="inline-flex min-h-[40px] items-center gap-1.5 rounded-lg bg-primary px-3 py-2 text-xs font-semibold text-white hover:brightness-110 disabled:opacity-50">
                <Save className="h-3.5 w-3.5" /> {savingRole === role ? "Kaydediliyor..." : "Kaydet"}
              </button>
            </div>
            <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
              {pages.map((pg) => (
                <PageCheck key={pg.key} label={pg.label}
                  checked={(rolePerms[role] || []).includes(pg.key)}
                  onChange={() => toggleRolePage(role, pg.key)}
                  testid={`role-perm-${role}-${pg.key}`} />
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* User-based */}
      <div className="rounded-2xl border border-border bg-white p-5 shadow-sm">
        <div className="mb-1 flex items-center gap-2">
          <Shield className="h-5 w-5 text-primary" />
          <h3 className="font-display text-lg font-bold">Kullanıcı Bazlı Yetkiler</h3>
        </div>
        <p className="mb-4 text-sm text-muted-foreground">Belirli bir kullanıcıya özel sayfa izinleri verin. "Rolünden miras al" açıkken rol ayarları geçerlidir.</p>

        <div className="mb-4">
          <label className="mb-1 block text-xs font-semibold text-muted-foreground">Kullanıcı</label>
          <select data-testid="perm-user-select" value={selUser} onChange={(e) => setSelUser(e.target.value)}
            className="min-h-[44px] w-full rounded-xl border border-border bg-white px-3.5 py-2.5 text-sm outline-none focus:border-primary sm:max-w-sm">
            <option value="">— Kullanıcı seçin —</option>
            {users.map((u) => (
              <option key={u.id} value={u.id}>{u.full_name} · {ROLE_LABELS[u.role] || u.role}</option>
            ))}
          </select>
        </div>

        {selectedUser && selectedUser.role === "admin" && (
          <div className="rounded-xl border border-dashed border-border bg-secondary/30 px-4 py-3 text-sm">
            <span className="font-semibold">{selectedUser.full_name}</span> bir yöneticidir ve her zaman tüm sayfalara erişir.
          </div>
        )}

        {selectedUser && selectedUser.role !== "admin" && (
          <div className="space-y-4">
            <label className="flex cursor-pointer items-center justify-between rounded-xl border border-border px-4 py-3">
              <div>
                <div className="text-sm font-semibold">Rolünden miras al</div>
                <div className="text-xs text-muted-foreground">Kapatırsanız bu kullanıcıya özel sayfalar seçebilirsiniz.</div>
              </div>
              <button type="button" data-testid="perm-user-inherit-toggle" onClick={() => setInherit((v) => !v)}
                className={`relative h-6 w-11 shrink-0 rounded-full transition-colors ${inherit ? "bg-primary" : "bg-slate-300"}`}>
                <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-all ${inherit ? "left-[22px]" : "left-0.5"}`} />
              </button>
            </label>

            <div className={`grid gap-2 sm:grid-cols-2 lg:grid-cols-3 ${inherit ? "pointer-events-none opacity-50" : ""}`}>
              {pages.map((pg) => (
                <PageCheck key={pg.key} label={pg.label} disabled={inherit}
                  checked={inherit ? (rolePerms[selectedUser.role] || []).includes(pg.key) : userPages.includes(pg.key)}
                  onChange={() => toggleUserPage(pg.key)}
                  testid={`user-perm-${pg.key}`} />
              ))}
            </div>

            <button data-testid="perm-user-save-btn" onClick={saveUser} disabled={savingUser}
              className="inline-flex min-h-[44px] items-center gap-2 rounded-xl bg-primary px-4 py-2.5 text-sm font-semibold text-white hover:brightness-110 disabled:opacity-50">
              <Save className="h-4 w-4" /> {savingUser ? "Kaydediliyor..." : "Kullanıcı Yetkilerini Kaydet"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
